import React, { useEffect, useState } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { AuthContext } from '../lib/auth';
import { useNavigate, useLocation } from 'react-router-dom';

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    let mounted = true;
    let authSubscription: ReturnType<typeof supabase.auth.onAuthStateChange>["data"]["subscription"] | null = null;

    const initAuth = async () => {
      try {
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        if (sessionError) {
          console.error('Session error:', sessionError);
        }

        if (mounted) {
          if (session?.user) {
            setUser(session.user);
          } else {
            setUser(null);
            if (!isPublicRoute(location.pathname) && location.pathname !== '/login') {
              navigate('/login');
            }
          }
        }

        const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
          if (mounted) {
            if (session?.user) {
              setUser(session.user);
              if (event === 'SIGNED_IN' && (location.pathname === '/login' || location.pathname === '/register')) {
                navigate('/');
              }
            } else {
              setUser(null);
              if (event === 'SIGNED_OUT') {
                navigate('/login');
              }
            }
          }
        });

        authSubscription = subscription;
      } catch (error) {
        console.error('Error initializing auth:', error);
        if (mounted) {
          setUser(null);
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    initAuth();

    return () => {
      mounted = false;
      if (authSubscription) {
        authSubscription.unsubscribe();
      }
    };
  }, [navigate, location.pathname]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        supabase.auth.getSession().then(async ({ data: { session } }) => {
          if (!session) {
            navigate('/login');
          } else {
            const { error } = await supabase.from('profiles').select('id').limit(1);
            if (error && error.code === '401') {
              await supabase.auth.signOut();
              navigate('/login');
            } else if (!error) {
              setUser(session.user);
            }
          }
        });
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [navigate]);

  const isPublicRoute = (path: string): boolean => {
    const publicRoutes = ['/', '/login', '/register', '/cgu', '/privacy', '/contact'];
    return publicRoutes.some(route => path.startsWith(route));
  };

  const sendOtp = async (email: string, createIfNotExists = true) => {
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        shouldCreateUser: createIfNotExists,
      },
    });
    if (error) {
      if (!createIfNotExists && (error.message?.toLowerCase().includes('user not found') || error.message?.toLowerCase().includes('email not confirmed') || error.status === 400 || error.status === 422)) {
        throw new Error('ACCOUNT_NOT_FOUND');
      }
      throw new Error('Impossible d\'envoyer le code. Veuillez vérifier votre adresse email et réessayer.');
    }
  };

  const verifyOtp = async (email: string, token: string) => {
    const { data, error } = await supabase.auth.verifyOtp({
      email,
      token,
      type: 'email',
    });
    if (error) {
      throw new Error('Code invalide ou expiré. Veuillez réessayer.');
    }
    if (data.user) {
      setUser(data.user);
    }
  };

  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      setUser(null);
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const value = {
    user,
    loading,
    sendOtp,
    verifyOtp,
    signOut,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
